import { Injectable, signal, computed, NgZone, inject, OnDestroy } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private readonly mediaListener = (e: MediaQueryListEvent) => {
    this.zone.run(() => {
      this.osPrefersDark.set(e.matches);
      if (this.preference() === 'system') {
        this.apply();
      }
    });
  };

  readonly preference = signal<ThemePreference>('system');
  private readonly osPrefersDark = signal(this.mediaQuery.matches);

  readonly isDark = computed(() => {
    const pref = this.preference();
    return pref === 'system' ? this.osPrefersDark() : pref === 'dark';
  });

  init(): void {
    const stored = localStorage.getItem('theme') as ThemePreference | null;
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      this.preference.set(stored);
    }
    this.apply();
    this.mediaQuery.addEventListener('change', this.mediaListener);
  }

  setTheme(pref: ThemePreference): void {
    this.preference.set(pref);
    localStorage.setItem('theme', pref);
    this.apply();
  }

  toggle(): void {
    this.setTheme(this.isDark() ? 'light' : 'dark');
  }

  private apply(): void {
    const resolved = this.isDark() ? 'dark' : 'light';
    document.documentElement.setAttribute('data-bs-theme', resolved);
  }

  ngOnDestroy(): void {
    this.mediaQuery.removeEventListener('change', this.mediaListener);
  }
}
